import React, { useState, useEffect, useRef } from 'react';
import { getWebSocketBaseUrl } from '../utils/api';

export function EventControl({ api, eventId, eventStateId }) {
    const [eventStatus, setEventStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [runData, setRunData] = useState({ time: '', penalties: '0', horseName: '' });
    const [connected, setConnected] = useState(false);
    const [wsError, setWsError] = useState(null);
    const [wsConnecting, setWsConnecting] = useState(false);
    const wsRef = useRef(null);
    const retryTimeoutRef = useRef(null);
    const retryCountRef = useRef(0);
    const maxRetries = 3;

    // WebSocket connection for real-time updates
    useEffect(() => {
        if (!eventStateId) return;

        const connectWebSocket = () => {
            setWsConnecting(true);
            setWsError(null);

            // Use the API's WebSocket connection method
            const ws = api.connectToEventState(eventStateId);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('WebSocket connected');
                setConnected(true);
                setWsConnecting(false);
                retryCountRef.current = 0;
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    if (message.type === 'status') {
                        setEventStatus(message.data);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onclose = (event) => {
                setConnected(false);
                setWsConnecting(false);

                // Only retry if it's not a normal closure and we still have retries left
                if (event.code !== 1000 && retryCountRef.current < maxRetries) {
                    retryCountRef.current++;
                    setWsError(`Connection lost. Retrying... (${retryCountRef.current}/${maxRetries})`);

                    // Clear any existing retry timeout
                    if (retryTimeoutRef.current) {
                        clearTimeout(retryTimeoutRef.current);
                    }

                    // Retry after 2 seconds
                    retryTimeoutRef.current = setTimeout(() => {
                        connectWebSocket();
                    }, 2000);
                } else if (retryCountRef.current >= maxRetries) {
                    setWsError('Failed to connect after multiple attempts. Please refresh the page.');
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                ws.close();
            };
        };

        // Initial fetch of event status
        const fetchStatus = async () => {
            try {
                const status = await api.getEventStatus(eventStateId);
                setEventStatus(status);
            } catch (err) {
                setError('Failed to fetch event status');
            }
        };

        fetchStatus();
        connectWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [api, eventStateId]);

    const handleStartEvent = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.startEvent(eventStateId, eventId);
            setSuccess('Event started!');
        } catch (err) {
            setError(`Failed to start event: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleStopEvent = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.stopEvent(eventStateId);
            setSuccess('Event stopped!');
        } catch (err) {
            setError(`Failed to stop event: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleNextClass = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.startNextClass(eventStateId);
            setSuccess('Next class started!');
        } catch (err) {
            setError(`Failed to start next class: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleNextParticipant = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.startNextParticipant(eventStateId);
            setSuccess('Next participant started!');
        } catch (err) {
            setError(`Failed to start next participant: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteRun = async (e) => {
        e.preventDefault();
        if (!runData.time) {
            setError('Please enter a time');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await api.completeRun(eventStateId, {
                participantId: eventStatus.currentParticipant.id,
                time: parseFloat(runData.time),
                penalties: parseFloat(runData.penalties),
                horseName: runData.horseName || eventStatus.currentParticipant.horses[0] || 'Unknown'
            });
            setSuccess('Run completed successfully!');
            setRunData({ time: '', penalties: '0', horseName: '' });
        } catch (err) {
            setError(`Failed to complete run: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Effect to pre-fill horse name when participant changes
    useEffect(() => {
        if (eventStatus?.currentParticipant?.horses?.[0]) {
            setRunData(prev => ({
                ...prev,
                horseName: eventStatus.currentParticipant.horses[0]
            }));
        }
    }, [eventStatus?.currentParticipant]);

    // Calculate runs until next drag
    const calculateRunsUntilDrag = (eventStatus) => {
        if (!eventStatus?.currentClass?.dragsEveryNRuns) return null;
        const dragsEveryNRuns = eventStatus.currentClass.dragsEveryNRuns;
        if (dragsEveryNRuns <= 0) return null;

        const runs = eventStatus.runs || [];
        const currentRunCount = runs.length;
        const runsUntilDrag = dragsEveryNRuns - (currentRunCount % dragsEveryNRuns);
        return runsUntilDrag;
    };

    if (!eventStatus) {
        return (
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center justify-center">
                    <div className="text-muted-foreground">Loading event status...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border bg-card p-6 shadow-sm space-y-6">
            {/* Connection Status */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className={`h-2 w-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-sm text-muted-foreground">
                        {connected ? 'Connected' : 'Disconnected'}
                    </span>
                </div>
                {wsError && (
                    <span className="text-sm text-destructive">{wsError}</span>
                )}
            </div>

            {/* Event Controls */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Event Controls</h3>
                    <div className="flex items-center space-x-2">
                        <div className={`h-2 w-2 rounded-full ${eventStatus.status === 'running' ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className="text-sm text-muted-foreground capitalize">{eventStatus.status}</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {eventStatus.status === 'not_started' && (
                        <button
                            onClick={handleStartEvent}
                            disabled={loading}
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
                        >
                            Start Event
                        </button>
                    )}
                    {eventStatus.status === 'running' && (
                        <>
                            <button
                                onClick={handleStopEvent}
                                disabled={loading}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-red-600 text-white hover:bg-red-700 h-10 px-4 py-2"
                            >
                                Stop Event
                            </button>
                            <button
                                onClick={handleNextClass}
                                disabled={loading}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                            >
                                Next Class
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Current Class Info */}
            {eventStatus.currentClass && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-foreground">Current Class</h3>
                        <span className="text-sm text-muted-foreground">{eventStatus.currentClass.name}</span>
                    </div>

                    {/* Drag Countdown */}
                    {calculateRunsUntilDrag(eventStatus) !== null && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-200">
                            <div className="flex items-center space-x-2">
                                <span className="text-orange-600">🚜</span>
                                <span className="text-sm font-medium text-orange-900">Next Arena Drag</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <span className="text-sm text-orange-700">in</span>
                                <span className="font-bold text-lg text-orange-900">{calculateRunsUntilDrag(eventStatus)}</span>
                                <span className="text-sm text-orange-700">runs</span>
                            </div>
                        </div>
                    )}

                    {/* Current Participant */}
                    {eventStatus.currentParticipant && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-base font-medium text-foreground">Current Participant</h4>
                                <button
                                    onClick={handleNextParticipant}
                                    disabled={loading}
                                    className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 py-2"
                                >
                                    Next Participant
                                </button>
                            </div>

                            {/* Special handling for drag */}
                            {eventStatus.currentParticipant.type === 'drag' ? (
                                <div className="p-4 rounded-lg bg-orange-50 border border-orange-200">
                                    <div className="flex items-center space-x-3 mb-3">
                                        <span className="text-2xl">🚜</span>
                                        <div>
                                            <h2 className="text-lg font-bold text-orange-900">Arena Drag</h2>
                                            <p className="text-sm text-orange-700">Preparing arena for next set of runs</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleNextParticipant}
                                        disabled={loading}
                                        className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-orange-600 text-white hover:bg-orange-700 h-10 px-4 py-2"
                                    >
                                        {loading ? 'Advancing...' : 'Next Rider'}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                                        <p className="text-lg font-semibold text-green-900">{eventStatus.currentParticipant.name}</p>
                                        <p className="text-sm text-green-700">{eventStatus.currentParticipant.horses?.[0] || 'No horse specified'}</p>
                                    </div>

                                    {/* Time Entry Form */}
                                    <form onSubmit={handleCompleteRun} className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div className="space-y-2">
                                                <label htmlFor="time" className="text-sm font-medium text-foreground">
                                                    Time (seconds)
                                                </label>
                                                <input
                                                    id="time"
                                                    type="number"
                                                    step="0.001"
                                                    value={runData.time}
                                                    onChange={e => setRunData({ ...runData, time: e.target.value })}
                                                    placeholder="e.g., 15.243"
                                                    disabled={loading}
                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="penalties" className="text-sm font-medium text-foreground">
                                                    Penalties (seconds)
                                                </label>
                                                <input
                                                    id="penalties"
                                                    type="number"
                                                    step="0.001"
                                                    value={runData.penalties}
                                                    onChange={e => setRunData({ ...runData, penalties: e.target.value })}
                                                    placeholder="e.g., 5"
                                                    disabled={loading}
                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="horseName" className="text-sm font-medium text-foreground">
                                                    Horse Name
                                                </label>
                                                <input
                                                    id="horseName"
                                                    type="text"
                                                    value={runData.horseName}
                                                    onChange={e => setRunData({ ...runData, horseName: e.target.value })}
                                                    placeholder="e.g., Lightning"
                                                    disabled={loading}
                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={loading || !runData.time}
                                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                                        >
                                            {loading ? 'Recording...' : 'Record Run'}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Success/Error Messages */}
            {success && (
                <div className="rounded-lg border border-green-500/50 bg-green-500/10 p-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-green-600">✓</span>
                        <p className="text-sm text-green-600">{success}</p>
                    </div>
                </div>
            )}
            {error && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                    <div className="flex items-center space-x-2">
                        <span className="text-destructive">⚠️</span>
                        <p className="text-sm text-destructive">{error}</p>
                    </div>
                </div>
            )}
        </div>
    );
} 